import React, { createContext, useMemo, ReactElement, useContext } from 'react';
import { Option } from 'fp-ts/lib/Option';

import Permissions from 'domain/entity/permissions';
import { ReadPermissions } from 'use-case/permissions/read/readPermissionsUseCase';
import { useCase, UseCaseState, UseCaseDispatch, CriteriaCallback } from 'adapter/in/common/hooks/useCase';

export type PermissionsState = UseCaseState & { permissions: Option<Permissions> };

export const PermissionsStateContext = createContext({} as PermissionsState);
export const usePermissions = (): PermissionsState => useContext(PermissionsStateContext);

type PermissionsDispatch = UseCaseDispatch & { setPermissions: (permissions: Permissions) => void; read: CriteriaCallback };

const PermissionsDispatchContext = createContext({} as PermissionsDispatch);
export const dispatchPermissions = (): PermissionsDispatch => useContext(PermissionsDispatchContext);

type PermissionsProviderType = {
    readPermissions: ReadPermissions;
    children: ReactElement;
};
export const PermissionsProvider = (props: PermissionsProviderType): ReactElement => {
    const { children, readPermissions } = props;
    const { isLoading, data, error, setLoading, setData, setError, execute } = useCase(readPermissions, false);
    const valuePermissionsState = useMemo(() => ({ isLoading, permissions: data, error }), [isLoading, data, error]);
    const valuePermissionsDispatch = useMemo(
        () => ({
            setLoading,
            setPermissions: setData,
            setError,
            read: execute,
        }),
        [setLoading, setData, setError]
    );
    return (
        <PermissionsStateContext.Provider value={valuePermissionsState}>
            <PermissionsDispatchContext.Provider value={valuePermissionsDispatch}>{children}</PermissionsDispatchContext.Provider>
        </PermissionsStateContext.Provider>
    );
};
